import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Send, Download, Edit2, Trash2, Eye } from "lucide-react";

/* --------------------
   Reusable Components
-------------------- */
function Input({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-bold uppercase tracking-widest text-slate-400">
        {label}
      </label>
      <input
        type={type}
        className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all bg-slate-50/30"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-bold uppercase tracking-widest text-slate-400">
        {label}
      </label>
      <select
        className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all bg-white cursor-pointer"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select Type</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

/* --------------------
   Helpers
-------------------- */
const buildLetterText = (form) => {
  if (form.letterType === "Relieving Letter") {
    return `OFFICIAL RELIEVING LETTER\n\nDear ${form.employeeName},\n\nThis is to formally relieve you from your duties as ${form.designation} in the ${form.department}, effective from ${form.lastWorkingDate}. We appreciate your contributions and wish you success ahead.\n\nIssued By: ${form.issuedBy || "HR / Manager"}\n\nRemarks: ${form.remarks || "-"}`;
  }
  if (form.letterType === "Termination Letter") {
    return `OFFICIAL TERMINATION NOTICE\n\nDear ${form.employeeName},\n\nThis letter is to inform you that your employment as ${form.designation} in the ${form.department} is terminated effective from ${form.lastWorkingDate} due to ${form.reason}.\n\nIssued By: ${form.issuedBy || "HR / Manager"}\n\nRemarks: ${form.remarks || "-"}`;
  }
  return "Please select a letter type and fill in the details to generate preview.";
};

const downloadAsText = (filename, text) => {
  const blob = new Blob([text], { type: "text/plain" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

/* --------------------
   Main Component
-------------------- */
export default function RelievingTerminationLetter() {
  const navigate = useNavigate();
  const [sentLetters, setSentLetters] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  const [form, setForm] = useState({
    sendEmail: "",
    letterType: "",
    employeeId: "",
    employeeName: "",
    designation: "",
    department: "",
    lastWorkingDate: "",
    reason: "",
    issuedBy: "",
    remarks: "",
  });

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePreview = () => {
    if (!form.letterType || !form.employeeName) {
      alert("Please select letter type and enter employee name.");
      return;
    }
    setShowPreview(true);
  };

  const handleSend = () => {
    const letter = {
      ...form,
      date: new Date().toLocaleString(),
      text: buildLetterText(form),
    };

    if (editingIndex !== null) {
      const updated = [...sentLetters];
      updated[editingIndex] = letter;
      setSentLetters(updated);
      setEditingIndex(null);
    } else {
      setSentLetters((prev) => [letter, ...prev]);
    }

    alert(`Letter successfully generated for ${form.sendEmail}`);
    setShowPreview(false);
    setForm({
      sendEmail: "", letterType: "", employeeId: "", employeeName: "",
      designation: "", department: "", lastWorkingDate: "", reason: "",
      issuedBy: "", remarks: "",
    });
  };

  const handleEdit = (index) => {
    const l = sentLetters[index];
    setForm(l);
    setEditingIndex(index);
    setShowPreview(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-8" style={robotoStyle}>
      <div className="max-w-6xl mx-auto">
        
        {/* Navigation & Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-slate-500 hover:text-indigo-600 mb-3 transition-colors text-sm font-medium"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <h1 className="text-2xl font-semibold text-slate-900" style={poppinsStyle}>
            Relieving & Termination Management
          </h1>
          <p className="text-sm text-slate-500">Issue formal exit documentation for employees.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Form Column */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-6" style={poppinsStyle}>
                Letter Specifications
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Select label="Document Type" value={form.letterType} onChange={(v) => handleChange("letterType", v)} options={["Relieving Letter", "Termination Letter"]} />
                <Input label="Employee ID" placeholder="EMP_001" value={form.employeeId} onChange={(v) => handleChange("employeeId", v)} />
                <Input label="Employee Name" placeholder="Full Name" value={form.employeeName} onChange={(v) => handleChange("employeeName", v)} />
                <Input label="Designation" placeholder="e.g. Senior Developer" value={form.designation} onChange={(v) => handleChange("designation", v)} />
                <Input label="Department" placeholder="e.g. Engineering" value={form.department} onChange={(v) => handleChange("department", v)} />
                <Input label="Last Working Date" type="date" value={form.lastWorkingDate} onChange={(v) => handleChange("lastWorkingDate", v)} />
                <Input label="Reason / Clause" placeholder="Reason for exit" value={form.reason} onChange={(v) => handleChange("reason", v)} />
                <Input label="Authorized Signatory" placeholder="Manager Name" value={form.issuedBy} onChange={(v) => handleChange("issuedBy", v)} />
              </div>

              <div className="mt-5">
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-1.5">Additional Remarks</label>
                <textarea 
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 h-28 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-sm bg-slate-50/30" 
                  placeholder="Note special achievements or handover details..."
                  value={form.remarks} 
                  onChange={(e) => handleChange("remarks", e.target.value)} 
                />
              </div>

              <div className="mt-8 flex flex-col md:flex-row items-end gap-4 border-t pt-6">
                <div className="flex-grow w-full">
                  <Input label="Recipient Email" placeholder="employee@company.com" type="email" value={form.sendEmail} onChange={(v) => handleChange("sendEmail", v)} />
                </div>
                <button 
                  onClick={handlePreview} 
                  className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-2.5 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-indigo-200"
                >
                  <Eye size={18} /> Preview
                </button>
              </div>
            </div>

            {/* Sent Letters Table */}
            {sentLetters.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="p-4 bg-slate-50 border-b border-slate-100 font-bold text-xs uppercase tracking-widest text-slate-500">
                  Recent Documentation
                </div>
                <div className="divide-y divide-slate-100">
                  {sentLetters.map((l, i) => (
                    <div key={i} className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:bg-slate-50/50 transition-colors">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">{l.employeeName}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${l.letterType.includes('Termination') ? 'bg-red-50 text-red-600' : 'bg-teal-50 text-teal-600'}`}>
                            {l.letterType}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">Sent to {l.sendEmail} on {l.date}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleEdit(i)} className="p-2 text-slate-400 hover:text-indigo-600 transition-colors" title="Edit"><Edit2 size={16} /></button>
                        <button onClick={() => downloadAsText(`Letter_${l.employeeName}.txt`, l.text)} className="p-2 text-slate-400 hover:text-green-600 transition-colors" title="Download"><Download size={16} /></button>
                        <button onClick={() => setSentLetters(prev => prev.filter((_, idx) => idx !== i))} className="p-2 text-slate-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Preview Column */}
          <div className="lg:col-span-1">
            {showPreview ? (
              <div className="bg-white rounded-2xl border-2 border-indigo-100 p-6 shadow-xl sticky top-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
                <div className="flex items-center gap-2 mb-6 text-indigo-600">
                  <FileText size={20} />
                  <span className="font-bold text-xs uppercase tracking-widest">Document Draft</span>
                </div>
                <pre className="whitespace-pre-wrap text-sm text-slate-600 leading-relaxed font-sans bg-slate-50 p-4 rounded-xl border border-slate-100 mb-6">
                  {buildLetterText(form)}
                </pre>

                <div className="space-y-3">
                  <button 
                    onClick={handleSend} 
                    disabled={!form.sendEmail} 
                    className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-indigo-600 disabled:bg-slate-200 transition-all flex items-center justify-center gap-2 shadow-lg"
                  >
                    <Send size={16} /> Finalize & Send
                  </button>
                  <button 
                    onClick={() => setShowPreview(false)} 
                    className="w-full text-slate-400 hover:text-slate-600 text-[10px] font-bold uppercase tracking-widest py-2"
                  >
                    Discard Draft
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-slate-100/50 border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center sticky top-8">
                <FileText className="mx-auto text-slate-300 mb-3" size={40} />
                <p className="text-slate-400 text-sm font-medium">Complete the form to<br/>preview the document.</p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}